/**
 * Phase 17 Plan 17-02 — EditKpiDialog
 *
 * Pre-loads KPI detail via useKpiDetailQuery on open.
 * Unit field is DISABLED when entryCount > 0 (with title tooltip).
 * 409 KPI_UNIT_IMMUTABLE is surfaced inline as a defensive measure.
 *
 * Props mirror CreateKpiDialog but also accept kpiId.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useKpiDetailQuery, useUpdateKpiMutation } from '@/hooks/useKpiQueries';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import { KPI_UNITS, type KpiUnit, type UpdateKpiBody } from '@/types/kpi';
import type { WorkOrderMachineItem } from '@/types/work-orders';

// ---------------------------------------------------------------------------
// Zod schema (same as Create)
// ---------------------------------------------------------------------------

const editKpiSchema = z
  .object({
    name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name must be at most 100 characters'),
    unit: z.enum([
      'Nos', 'Meters', 'RunningMeters', 'Hours', 'Kilometers',
      'Liters', 'Kilograms', 'Units', 'Trips', 'Custom',
    ] as const),
    customUnitText: z.string().max(50, 'Custom unit must be at most 50 characters').optional(),
    description: z.string().max(500, 'Description must be at most 500 characters').optional(),
    targetPerShift: z.preprocess(
      (v) => (v === '' || v === undefined || v === null ? undefined : Number(v)),
      z.number().positive('Target must be positive').optional(),
    ),
    applicableMachineIds: z.array(z.number()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.unit === 'Custom') {
      if (data.customUnitText === undefined || data.customUnitText.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is required when unit is Custom',
        });
      }
    }
  });

type EditKpiFormValues = z.infer<typeof editKpiSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EditKpiDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly workOrderId: number;
  readonly kpiId: number;
  readonly workOrderMachines: readonly WorkOrderMachineItem[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditKpiDialog({
  open,
  onOpenChange,
  workOrderId,
  kpiId,
  workOrderMachines,
}: EditKpiDialogProps): JSX.Element {
  const mutation = useUpdateKpiMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const detailQuery = useKpiDetailQuery(workOrderId, kpiId, open);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditKpiFormValues>({
    resolver: zodResolver(editKpiSchema),
    defaultValues: {
      name: '',
      unit: 'Nos',
      customUnitText: '',
      description: '',
      targetPerShift: undefined,
      applicableMachineIds: workOrderMachines.map((m) => m.machineId),
    },
  });

  // Populate form when detail loads
  useEffect(() => {
    if (detailQuery.data !== undefined) {
      const d = detailQuery.data;
      reset({
        name: d.name,
        unit: d.unit,
        customUnitText: d.customUnitText ?? '',
        description: d.description ?? '',
        targetPerShift: d.targetPerShift ?? undefined,
        applicableMachineIds:
          d.applicableMachineIds !== undefined
            ? [...d.applicableMachineIds]
            : (d.applicableMachines?.map((m) => m.machineId) ?? []),
      });
    }
  }, [detailQuery.data, reset]);

  const watchedUnit = watch('unit');
  const watchedMachineIds = watch('applicableMachineIds') ?? [];
  const entryCount = detailQuery.data?.entryCount ?? 0;
  const unitDisabled = entryCount > 0;

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    onOpenChange(false);
  }

  function onSubmit(values: EditKpiFormValues): void {
    setServerError(null);
    const body: UpdateKpiBody = {
      name: values.name,
      unit: values.unit,
      ...(values.unit === 'Custom' && values.customUnitText
        ? { customUnitText: values.customUnitText }
        : {}),
      ...(values.description && values.description.trim() !== ''
        ? { description: values.description }
        : {}),
      ...(values.targetPerShift !== undefined ? { targetPerShift: values.targetPerShift } : {}),
      ...(values.applicableMachineIds !== undefined && values.applicableMachineIds.length > 0
        ? { applicableMachineIds: values.applicableMachineIds }
        : {}),
    };
    mutation.mutate(
      { woId: workOrderId, kpiId, body },
      {
        onSuccess: () => {
          toast.success('KPI updated');
          handleClose();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Failed to update KPI', { description: msg });
        },
      },
    );
  }

  function toggleMachine(machineId: number, checked: boolean): void {
    const current = watchedMachineIds;
    const next = checked
      ? [...current, machineId]
      : current.filter((id) => id !== machineId);
    setValue('applicableMachineIds', next);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="edit-kpi-dialog">
        <DialogHeader>
          <DialogTitle>Edit KPI</DialogTitle>
          <DialogDescription>
            Update KPI details.
            {unitDisabled && (
              <span className="ml-1 text-muted-foreground">
                Unit cannot be changed — {entryCount} {entryCount === 1 ? 'entry' : 'entries'} reference this KPI.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {detailQuery.isLoading && (
          <p className="text-sm text-muted-foreground py-4" data-testid="edit-kpi-loading">
            Loading KPI details…
          </p>
        )}

        {detailQuery.data !== undefined && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-1">
              <Label htmlFor="edit-kpi-name">Name</Label>
              <Input
                id="edit-kpi-name"
                data-testid="edit-kpi-name-input"
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-destructive" data-testid="edit-kpi-name-error">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Unit — disabled when entries exist */}
            <div className="space-y-1">
              <Label htmlFor="edit-kpi-unit">Unit</Label>
              <div
                title={
                  unitDisabled
                    ? `Cannot change unit — ${String(entryCount)} ${entryCount === 1 ? 'entry references' : 'entries reference'} this KPI`
                    : undefined
                }
              >
                <Select
                  value={watchedUnit}
                  onValueChange={(v) => { if (!unitDisabled) setValue('unit', v as KpiUnit); }}
                  disabled={unitDisabled}
                >
                  <SelectTrigger
                    id="edit-kpi-unit"
                    data-testid="edit-kpi-unit-trigger"
                    disabled={unitDisabled}
                    aria-disabled={unitDisabled}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KPI_UNITS.map((u) => (
                      <SelectItem key={u} value={u} data-testid={`edit-kpi-unit-option-${u}`}>
                        {u === 'RunningMeters' ? 'Running Meters' : u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.unit && (
                <p className="text-sm text-destructive" data-testid="edit-kpi-unit-error">
                  {errors.unit.message}
                </p>
              )}
            </div>

            {/* Custom unit text */}
            {watchedUnit === 'Custom' && (
              <div className="space-y-1">
                <Label htmlFor="edit-kpi-custom-unit">Custom Unit Text</Label>
                <Input
                  id="edit-kpi-custom-unit"
                  data-testid="edit-kpi-custom-unit-input"
                  {...register('customUnitText')}
                  disabled={unitDisabled}
                />
                {errors.customUnitText && (
                  <p className="text-sm text-destructive" data-testid="edit-kpi-custom-unit-error">
                    {errors.customUnitText.message}
                  </p>
                )}
              </div>
            )}

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="edit-kpi-description">Description (optional)</Label>
              <Textarea
                id="edit-kpi-description"
                data-testid="edit-kpi-description-input"
                {...register('description')}
                rows={2}
              />
            </div>

            {/* Target per shift */}
            <div className="space-y-1">
              <Label htmlFor="edit-kpi-target">Target per Shift (optional)</Label>
              <Input
                id="edit-kpi-target"
                data-testid="edit-kpi-target-input"
                type="number"
                step="any"
                min="0"
                {...register('targetPerShift')}
              />
              {errors.targetPerShift && (
                <p className="text-sm text-destructive" data-testid="edit-kpi-target-error">
                  {errors.targetPerShift.message}
                </p>
              )}
            </div>

            {/* Applicable machines */}
            {workOrderMachines.length > 0 && (
              <div className="space-y-2">
                <Label>Applicable Machines</Label>
                <div className="space-y-1" data-testid="edit-kpi-machines-checklist">
                  {workOrderMachines.map((m) => (
                    <div key={m.machineId} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-kpi-machine-${String(m.machineId)}`}
                        data-testid={`edit-kpi-machine-checkbox-${String(m.machineId)}`}
                        checked={watchedMachineIds.includes(m.machineId)}
                        onCheckedChange={(checked) => {
                          toggleMachine(m.machineId, checked === true);
                        }}
                      />
                      <Label
                        htmlFor={`edit-kpi-machine-${String(m.machineId)}`}
                        className="font-normal cursor-pointer"
                      >
                        {formatMachineName(m.machineName, m.vehicleNumber, m.machineCode)}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Server error */}
            {serverError !== null && (
              <div
                className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="edit-kpi-error"
              >
                {serverError}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={mutation.isPending}
                data-testid="edit-kpi-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending}
                data-testid="edit-kpi-submit"
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
